import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styles from './styles.module.scss'
import { useSelector, useDispatch } from 'react-redux'
import { deleteWithdraw } from '../../../../../Redux/withdraws/withdrawsSlice'
import {
  formatForKosovoUI,
  getTiraneDateString,
  getThisMonthRangeTirane,
} from '../../../../../utils/dateFormatter'

const CARRY_OVER_DESC = 'Kaluar nga dita e mëparshme'
const RETRO_CARRY_OVER_DESC = 'Kaluar nga dita e mëparshme (retroaktiv)'

function isCarryOverDescription(description) {
  return description === CARRY_OVER_DESC || description === RETRO_CARRY_OVER_DESC
}

/** Normalize transaction date to YYYY-MM-DD for comparison (string or Firestore Timestamp). */
function getNormalizedTransactionDate(change) {
  const raw = change?.date
  if (raw == null) return ''
  if (typeof raw === 'string') return String(raw).trim().slice(0, 10)
  if (typeof raw.toDate === 'function') return getTiraneDateString(raw.toDate())
  return ''
}

/** True if this record is a system carry-over and must not be deleted. */
function isSystemCarryOver(change) {
  return (
    isCarryOverDescription(change?.description) ||
    change?.creator === 'system'
  )
}

const DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'Të Gjitha' },
  { value: 'today', label: 'Sot' },
  { value: 'this_month', label: 'Këtë Muaj' },
]

const ViewCashierHistory = ({ close }) => {
  const dispatch = useDispatch()
  const { withdraws } = useSelector((state) => state.withdraws)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFilter, setDateFilter] = useState('all')

  const changes = withdraws ?? []

  // Step 1: Apply date filter first (before sort and search)
  const todayStr = getTiraneDateString()
  const thisMonth = getThisMonthRangeTirane()

  const dateFilteredChanges = changes.filter((change) => {
    if (dateFilter === 'all') return true
    const norm = getNormalizedTransactionDate(change)
    if (!norm) return false
    if (dateFilter === 'today') return norm === todayStr
    if (dateFilter === 'this_month') return norm >= thisMonth.start && norm <= thisMonth.end
    return true
  })

  // Enhanced sorting by date AND time (newest first)
  const sortedChanges = [...dateFilteredChanges].sort((a, b) => {
    const dateA = new Date(a.date)
    const dateB = new Date(b.date)
    if (dateA.getTime() !== dateB.getTime()) return dateB - dateA
    if (a.id && b.id) return b.id.localeCompare(a.id)
    return 0
  })

  // Display dates as DD/MM/YYYY (Kosovo standard)
  const formatDate = (dateString) => formatForKosovoUI(dateString ?? '')

  // Search filter (applied after date filter)
  const filteredChanges = sortedChanges.filter((change) => {
    const searchLower = searchTerm.toLowerCase()
    const description = change.description?.toLowerCase() || ''
    const amount = String(change.amount || '')
    const date = formatDate(change.date).toLowerCase()
    return (
      description.includes(searchLower) ||
      amount.includes(searchLower) ||
      date.includes(searchLower)
    )
  })

  const handleDelete = (change) => {
    dispatch(deleteWithdraw({ id: change.id }))
  }

  // Step 2: Correct summary math (deposits, withdraws, net)
  const totalDeposits = filteredChanges
    .filter((c) => c.type !== 'withdraw')
    .reduce((sum, c) => sum + Number(c.amount || 0), 0)
  const totalWithdraws = filteredChanges
    .filter((c) => c.type === 'withdraw')
    .reduce((sum, c) => sum + Number(c.amount || 0), 0)
  const netBalance = totalDeposits - totalWithdraws

  // Step 4: Dev-only debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Cashier History Debug:', {
      totalWithdraws: withdraws?.length || 0,
      dateFilter,
      dateFiltered: dateFilteredChanges.length,
      filteredChanges: filteredChanges.length,
      searchTerm: searchTerm || 'none',
    })
  }

  return (
    <div className={styles.popup_wrapper}>
      <div className={styles.popup_header}>
        <h2>Ndryshimet e Arkës</h2>
        <div className={styles.header_controls}>
          <div className={styles.search_container}>
            <input
              type="text"
              placeholder="Kërko..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.search_input}
            />
          </div>
          <button className={styles.close_btn} onClick={close}>
            ✖
          </button>
        </div>
      </div>

      {/* Step 1: Date filter UI */}
      <div className={styles.date_filter_section}>
        <span className={styles.date_filter_label}>Kohë:</span>
        <div className={styles.date_filter_buttons}>
          {DATE_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={
                dateFilter === opt.value
                  ? `${styles.date_filter_btn} ${styles.date_filter_btn_active}`
                  : styles.date_filter_btn
              }
              onClick={() => setDateFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Summary with three values */}
      <div className={styles.summary_section}>
        <div className={styles.summary_item}>
          <span className={styles.summary_label}>Totali i Regjistrimeve</span>
          <span className={styles.summary_value}>{filteredChanges.length}</span>
        </div>
        <div className={styles.summary_item}>
          <span className={styles.summary_label}>Total Depozitime</span>
          <span className={styles.summary_value}>
            € {totalDeposits.toFixed(2)}
          </span>
        </div>
        <div className={styles.summary_item}>
          <span className={styles.summary_label}>Total Tërheqje</span>
          <span className={`${styles.summary_value} ${styles.summary_withdraw}`}>
            € {totalWithdraws.toFixed(2)}
          </span>
        </div>
        <div className={styles.summary_item}>
          <span className={styles.summary_label}>Bilanci Neto</span>
          <span
            className={`${styles.summary_value} ${netBalance < 0 ? styles.summary_negative : ''}`}
          >
            € {netBalance.toFixed(2)}
          </span>
        </div>
      </div>

      <div className={styles.popup_body}>
        <div className={styles.changes_list}>
          {filteredChanges.map((change, index) => (
            <div
              key={change.id || index}
              className={styles.change_item}
            >
              <div className={styles.change_main}>
                <div className={styles.change_date}>
                  📅 {formatDate(change.date)}
                </div>
                <div className={styles.change_description}>
                  {change.description ||
                    (change.type === 'withdraw' ? 'Tërheqje' : 'Depozitim')}
                </div>
                {change.type && (
                  <div className={styles.change_type}>
                    {change.type === 'withdraw'
                      ? '💰 Tërheqje'
                      : '💳 Depozitim'}
                  </div>
                )}
              </div>
              <div className={styles.change_actions}>
                <div
                  className={`${styles.change_amount} ${change.type === 'withdraw' ? styles.withdraw : ''}`}
                >
                  € {change.amount}
                </div>
                {/* Step 3: Protect system carry-over from deletion */}
                {isSystemCarryOver(change) ? (
                  <button
                    type="button"
                    className={styles.lock_button}
                    disabled
                    title="Transferim i sistemit (Nuk mund të fshihet)"
                  >
                    🔒
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.delete_button}
                    onClick={() => handleDelete(change)}
                    title="Fshi"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))}
          {filteredChanges.length === 0 && (
            <div className={styles.no_changes}>
              {searchTerm
                ? 'Nuk u gjetën rezultate'
                : 'Nuk ka ndryshime'}
            </div>
          )}
        </div>
      </div>
      <div className={styles.popup_footer}>
        <button onClick={close} className={styles.close_button}>
          Mbyll
        </button>
      </div>
    </div>
  )
}

ViewCashierHistory.propTypes = {
  close: PropTypes.func.isRequired,
  data: PropTypes.object,
}

export default ViewCashierHistory
